/**
 * Jobs API Service
 * Provides data for historical jobs, switching between mock and real APIs
 * based on the USE_MOCK_DATA environment variable
 */

import { USE_MOCK_DATA, mockApiService } from './mockData';
import api from './api';
import { API_ENDPOINTS } from '../../config/api';

/**
 * Jobs API that automatically uses mock or real data
 */
export const jobsApi = {
  /**
   * Get list of historical jobs with filtering and pagination
   * @param {Object} options - Query options
   * @param {string} options.jobType - Filter by building type
   * @param {string} options.location - Filter by location
   * @param {string} options.search - Search term for project name or customer
   * @param {number} options.page - Page number (1-based)
   * @param {number} options.limit - Items per page
   */
  getJobs: async (options = {}) => {
    if (USE_MOCK_DATA) {
      return mockApiService.getJobs(options);
    }

    const params = new URLSearchParams();
    if (options.jobType) params.append('jobType', options.jobType);
    if (options.location) params.append('location', options.location);
    if (options.search) params.append('search', options.search);
    if (options.page) params.append('page', options.page);
    if (options.limit) params.append('limit', options.limit);

    const query = params.toString();
    return api.get(`${API_ENDPOINTS.JOBS}${query ? `?${query}` : ''}`);
  },

  /**
   * Get a single job by ID
   * @param {string} id - Job ID
   */
  getJobById: async (id) => {
    if (USE_MOCK_DATA) {
      return mockApiService.getJobById(id);
    }
    return api.get(API_ENDPOINTS.JOB_DETAIL(id));
  },

  /**
   * Get list of building types for filter dropdown
   */
  getJobTypes: async () => {
    if (USE_MOCK_DATA) {
      return mockApiService.getJobTypes();
    }
    return api.get('/job-types');
  },

  /**
   * Get CSV upload history
   */
  getCsvUploads: async () => {
    if (USE_MOCK_DATA) {
      return mockApiService.getCsvUploads();
    }
    return api.get('/uploads');
  },

  /**
   * Request a presigned URL for CSV upload
   * @param {string} fileName - Name of the file to upload
   */
  getUploadUrl: async (fileName) => {
    if (USE_MOCK_DATA) {
      // Return a fake URL for mock mode
      return {
        uploadUrl: 'mock://upload',
        uploadId: `upload-${Date.now()}`,
      };
    }
    return api.post(API_ENDPOINTS.UPLOAD_CSV, { fileName });
  },

  /**
   * Upload CSV file (for mock mode, simulates the upload)
   * @param {File} file - The CSV file to upload
   * @param {Function} onProgress - Progress callback
   */
  uploadCsv: async (file, onProgress) => {
    if (USE_MOCK_DATA) {
      // Simulate upload progress
      for (let i = 0; i <= 100; i += 10) {
        await new Promise((r) => setTimeout(r, 200));
        onProgress?.(i);
      }
      return mockApiService.uploadCsv(file);
    }

    // Real upload would use presigned URL
    const { uploadUrl, uploadId } = await jobsApi.getUploadUrl(file.name);

    // Upload to S3
    await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': 'text/csv',
      },
    });

    // Trigger processing
    return api.post(API_ENDPOINTS.PROCESS_CSV, { uploadId, fileName: file.name });
  },

  /**
   * Get upload status (for polling during processing)
   * @param {string} uploadId - Upload ID to check
   */
  getUploadStatus: async (uploadId) => {
    if (USE_MOCK_DATA) {
      return mockApiService.getUploadStatus(uploadId);
    }
    return api.get(`/uploads/${uploadId}/status`);
  },

  /**
   * Download CSV template
   */
  getTemplate: () => {
    // CSV template for commercial construction
    const headers = [
      'projectName',
      'customer',
      'jobType',
      'squareFootage',
      'location',
      'completedDate',
      'finalCost',
      'margin',
      'outcome',
      'sitework',
      'concrete',
      'masonry',
      'steel',
      'carpentry',
      'roofing',
      'interiorFinishes',
      'mep',
      'generalConditions',
      'notes',
    ];

    const exampleRow = [
      'Whole Foods Market - Downtown',
      'Whole Foods Market Inc.',
      'Retail',
      '45000',
      'Cincinnati, OH',
      '2023-06-15',
      '4500000',
      '12.5',
      'profitable',
      '360000',
      '540000',
      '225000',
      '675000',
      '315000',
      '270000',
      '810000',
      '990000',
      '315000',
      'Standard grocery store build',
    ];

    const csv = [headers.join(','), exampleRow.join(',')].join('\n');
    return csv;
  },
};

export default jobsApi;
